import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { filter } from 'lodash/fp';
import styled from '@emotion/styled';

import { SEARCH_METHODS } from 'lib/config';
import { formatGender, formatPhotoUrl } from 'lib/formatters';

import Box from 'components/box';
import DetailSection from 'components/detail-section';
import { Text } from 'components/text';
import DotDivider from 'components/dividers/dot-divider';
import Indicator from 'components/indicator';
import Button from 'components/button';

import MemberPhoto from './member-photo';

const MemberHouseholdDetails = ({ linkBaseUrl, householdMembers, currentMember, searchMethod, onDeleteMember }) => {
  const householdWithoutMember = filter(m => m !== currentMember)(householdMembers);
  const searchMethodUrlParam = SEARCH_METHODS[searchMethod] || '';
  
  const handleDeleteClick = async (member, event) => {
    event.preventDefault();
    event.stopPropagation();
    
    // Only allow deletion if the member is a household head
    const relationshipToHead = member.relationshipToHead || member.relationship_to_head;
    if (relationshipToHead !== 'SELF') {
      window.alert('Only household heads can be deleted. This will delete the entire household and all related records.');
      return;
    }
    
    const confirmMessage = `Are you sure you want to delete this household (headed by ${member.fullName}) and all related records? This action cannot be undone.`;
    if (window.confirm(confirmMessage) && onDeleteMember) {
      // Ensure member ID is a string (backend expects string or integer)
      const memberId = String(member.id);
      // Pass a flag to indicate this deletion is from household details
      onDeleteMember(memberId, true);
    }
  };
  
  return (
    <DetailSection
      title={`Covered under this household (${householdWithoutMember.length})`}
      titleProps={{ fontSize: 3 }}
    >
      {householdWithoutMember.map(member => (
        <HouseholdMemberContainer key={member.id}>
          <Box flex alignItems="center" justifyContent="space-between" marginBottom={3}>
            <Link to={`${linkBaseUrl}/${member.id}/${searchMethodUrlParam}`} style={{ flex: 1, textDecoration: 'none' }}>
              <Box flex alignItems="center">
                <Box marginRight={4}>
                  <MemberPhoto src={formatPhotoUrl(member.photoUrl)} name={member.fullName} small />
                </Box>
                <Box paddingTop={1}>
                  <Box flex alignItems="center">
                    <Text>{member.fullName}</Text>
                    {member.archivedReason && (
                      <Box marginLeft={2}><Indicator type="inactive" /></Box>
                    )}
                  </Box>
                  <Text color="gray.6" fontSize={2}>
                    <span>{formatGender(member.gender)}</span>
                    <DotDivider />
                    <span>{member.age}</span>
                  </Text>
                </Box>
              </Box>
            </Link>
            {onDeleteMember && (
              <Box marginLeft={3}>
                <Button
                  small
                  inline
                  primaryRed
                  onClick={(e) => handleDeleteClick(member, e)}
                >
                  Delete
                </Button>
              </Box>
            )}
          </Box>
        </HouseholdMemberContainer>
      ))}
    </DetailSection>
  );
};

export default MemberHouseholdDetails;

MemberHouseholdDetails.propTypes = {
  currentMember: PropTypes.shape({}).isRequired,
  householdMembers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  linkBaseUrl: PropTypes.string.isRequired,
  searchMethod: PropTypes.string,
  onDeleteMember: PropTypes.func,
};

MemberHouseholdDetails.defaultProps = {
  searchMethod: null,
  onDeleteMember: null,
};

const HouseholdMemberContainer = styled.div`
  &:hover {
    opacity: 0.6;
  }
   ${props => props.active && `
    opacity: 0.6;
  `}
`;
